import {config} from "@/constants/config";
import {useAuthStore} from "@/store/authStore";
import type {Result} from "@/types/api.types";

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

interface RequestOptions {
  body?: unknown;
  params?: Record<string, string | number | boolean | undefined>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

const buildUrl = (
  base: string,
  path: string,
  params?: Record<string, string | number | boolean | undefined>
): string => {
  const url = new URL(`${base}${path}`);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
};

const getAuthHeaders = (): Record<string, string> => {
  const token = useAuthStore.getState().accessToken;
  return token ? {Authorization: `Bearer ${token}`} : {};
};

const request = async <T>(
  method: HttpMethod,
  baseUrl: string,
  path: string,
  options: RequestOptions = {}
): Promise<Result<T>> => {
  const url = buildUrl(baseUrl, path, options.params);

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...getAuthHeaders(),
    ...options.headers,
  };

  try {
    const response = await fetch(url, {
      method,
      headers,
      body: options.body ? JSON.stringify(options.body) : undefined,
      signal: options.signal,
    });

    if (response.status === 401) {
      useAuthStore.getState().logout();
      return {ok: false, error: "Session expired. Please log in again.", status: 401};
    }

    const data = await response.json();

    if (!response.ok) {
      return {ok: false, error: data.message ?? "Request failed", status: response.status};
    }

    return {ok: true, data: data as T};
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      return {ok: false, error: "Request cancelled"};
    }
    return {ok: false, error: "Network error. Please check your connection."};
  }
};

const createClient = (baseUrl: string) => ({
  get: <T>(path: string, options?: RequestOptions) => request<T>("GET", baseUrl, path, options),
  post: <T>(path: string, options?: RequestOptions) => request<T>("POST", baseUrl, path, options),
  put: <T>(path: string, options?: RequestOptions) => request<T>("PUT", baseUrl, path, options),
  patch: <T>(path: string, options?: RequestOptions) => request<T>("PATCH", baseUrl, path, options),
  delete: <T>(path: string, options?: RequestOptions) => request<T>("DELETE", baseUrl, path, options),
});

export const coreApi = createClient(config.apiBaseUrl);
export const walletApi = createClient(config.walletApiUrl);
export const onboardingApi = createClient(config.onboardingApiUrl);
